'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { fetchFlowDocumentProcessingRealtimeToken } from '@lib/flows/utils/fetch-flow-document-processing-realtime-token.client';
import { reportDocumentProcessingRealtimeTokenFailure } from '@lib/flows/utils/report-document-processing-realtime-token-failure.client';

/**
 * Mints the document-processing realtime token without blocking callers when
 * mint/fetch fails. Successful mint enables Inngest `useRealtime`; failures
 * soft-fail to durable flow polling elsewhere in the stack.
 */
export function useFlowDocumentProcessingRealtimeToken(args) {
  const $ = _c(12);
  const {
    flowId,
    mintEnabled,
    observabilitySurface: t0
  } = args;
  const observabilitySurface = t0 === undefined ? "use_realtime" : t0;
  const [storedToken, setStoredToken] = useState(null);
  const [mintFailedFlowId, setMintFailedFlowId] = useState(null);
  let t1;
  let t2;
  if ($[0] !== flowId || $[1] !== mintEnabled || $[2] !== observabilitySurface) {
    t1 = () => {
      setStoredToken(null);
      setMintFailedFlowId(null);
      if (!flowId || !mintEnabled) {
        return;
      }
      let cancelled = false;
      const mintFlowId = flowId;
      fetchFlowDocumentProcessingRealtimeToken(mintFlowId).then(token => {
        if (!cancelled) {
          setStoredToken({
            flowId: mintFlowId,
            ...token
          });
        }
      }).catch(error => {
        if (!cancelled) {
          reportDocumentProcessingRealtimeTokenFailure({
            flowId: mintFlowId,
            error,
            surface: observabilitySurface
          });
          setMintFailedFlowId(mintFlowId);
        }
      });
      return () => {
        cancelled = true;
      };
    };
    t2 = [flowId, mintEnabled, observabilitySurface];
    $[0] = flowId;
    $[1] = mintEnabled;
    $[2] = observabilitySurface;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== flowId || $[6] !== storedToken) {
    t3 = storedToken != null && flowId != null && storedToken.flowId === flowId ? {
      key: storedToken.key,
      apiBaseUrl: storedToken.apiBaseUrl
    } : null;
    $[5] = flowId;
    $[6] = storedToken;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const clientToken = t3;
  const mintFailed = mintFailedFlowId === flowId;
  const realtimeSubscribeEnabled = mintEnabled && Boolean(flowId) && clientToken != null && !mintFailed;
  let t4;
  if ($[8] !== clientToken || $[9] !== mintFailed || $[10] !== realtimeSubscribeEnabled) {
    t4 = {
      clientToken,
      mintFailed,
      realtimeSubscribeEnabled
    };
    $[8] = clientToken;
    $[9] = mintFailed;
    $[10] = realtimeSubscribeEnabled;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}